import { useState, useCallback, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Button, cn } from '@/shared';
import { useMarketTickersQuery } from '@/features/market';
import { useUpbitWebSocket } from '@/entities/market';
import { currencyQueryOptions } from '@/entities/currency';
import { exchangeDisplayState } from '@/entities/currency/model/display-state';
import { t } from '@/i18n';

import type { UpbitTicker } from '@/entities/market/types';
import { formatLocalizedNumber } from '@/shared/lib/utils/format';
import { signedFixed } from '@/shared/lib/utils/price-tone';

const SUMMARY_MARKETS = ['KRW-BTC', 'KRW-ETH'];

export const MarketSummaryStrip = () => {
  const { data: restData } = useMarketTickersQuery(SUMMARY_MARKETS);
  const {
    data: exchangeRateData,
    isError: isRateError,
    error: rateError,
    refetch: refetchRate,
  } = useQuery(currencyQueryOptions.getExchangeRateToday());

  const [liveData, setLiveData] = useState<Record<string, UpbitTicker>>({});

  const handleTickerUpdate = useCallback((ticker: UpbitTicker) => {
    setLiveData(prev => ({ ...prev, [ticker.market]: ticker }));
  }, []);

  useUpbitWebSocket({
    markets: SUMMARY_MARKETS,
    onTickerUpdate: handleTickerUpdate,
    enabled: true,
  });

  const krwRate = exchangeRateData?.rateList?.find(r => r.name === 'KRW');
  // TRAD-3567 — 환율 조회가 실패하면 USD/KRW 칸만 조용히 빠졌다. 홈 환율 카드와 같은 3상태 규칙·같은 문구로 알린다.
  const rateState = exchangeDisplayState(Boolean(krwRate), Boolean(isRateError));
  useEffect(() => {
    if (isRateError) console.error('[header.market-summary] exchange rate load failed', rateError);
  }, [isRateError, rateError]);

  if (!restData || restData.length === 0) return null;

  return (
    // TRAD-3600 — 헤더 폭이 모자라면 이 띠가 줄어든다(min-w-0). 🔴 안 들어가는 «칸» 은 통째로 숨는다:
    //   flex-wrap 이라 안 들어가는 칸은 다음 줄로 내려가고, 높이를 한 줄(h-4 = text-xs 줄 높이 16px)로 고정 + overflow-hidden 이라 그 줄은 보이지 않는다.
    //   종전(한 줄 + overflow-hidden)은 숫자 «중간» 에서 잘려 «USD/KRW 1,3» · «+0.» 처럼 다른 값으로 읽히는 조각이 남았다.
    //   content-start = 줄을 위에서부터 쌓는다(가운데 정렬이면 두 줄이 반씩 걸쳐 보인다). 뒤 칸부터 숨는다: USD/KRW → ETH → BTC.
    <div className="hidden md:flex h-4 min-w-0 flex-wrap content-start items-center gap-x-4 gap-y-2 overflow-hidden whitespace-nowrap text-xs">
      {SUMMARY_MARKETS.map(market => {
        const rest = restData.find((t: UpbitTicker) => t.market === market);
        const live = liveData[market];
        const data = live ? { ...rest, ...live } : rest;
        if (!data) return null;

        const label = market.split('-')[1];
        const price = data.trade_price?.toLocaleString() ?? '-';
        const changeRate = data.change_rate ?? 0;
        const isRise = data.change === 'RISE';
        const isFall = data.change === 'FALL';

        return (
          <div key={market} className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">{label}</span>
            <span className="text-foreground font-medium tabular-nums">{price}</span>
            <span
              className={cn(
                'font-medium tabular-nums',
                isRise && 'text-price-rise',
                isFall && 'text-price-fall',
                !isRise && !isFall && 'text-muted-foreground',
              )}
            >
              {/* TRAD-3670 — 반올림 0 은 부호 없이 */}
              {signedFixed(changeRate * 100, 2)}%
            </span>
          </div>
        );
      })}

      {/* 세로선과 칸은 «한 묶음» 이다 — 따로 두면 칸만 다음 줄로 내려가고 세로선이 끝에 혼자 남는다. */}
      {rateState === 'failed' && (
        <div className="flex items-center gap-4" data-testid="usd-krw-group">
          <div className="w-px h-3 bg-border" />
          {/* 값 자리에 «다시 시도» — 같은 칸 규격(라벨 + 값) 그대로, 무엇이 실패했는지는 title·접근성 이름에(홈 카드와 같은 문구). */}
          <div className="flex items-center gap-1.5" data-testid="usd-krw-failed">
            <span className="text-muted-foreground font-medium">USD/KRW</span>
            <Button
              type="button"
              variant="link"
              onClick={() => void refetchRate?.()}
              title={t('home.exchange_rate.load_failed')}
              aria-label={`${t('home.exchange_rate.load_failed')} — ${t('error.generic.retry')}`}
              className="h-auto p-0 text-xs font-medium"
            >
              {t('error.generic.retry')}
            </Button>
          </div>
        </div>
      )}

      {rateState === 'list' && krwRate && (
        <div className="flex items-center gap-4" data-testid="usd-krw-group">
          <div className="w-px h-3 bg-border" />
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">USD/KRW</span>
            <span className="text-foreground font-medium tabular-nums">
              {formatLocalizedNumber(krwRate.rate, { maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
